import React from "react";
import { Button, Checkbox, Form, Input, InputNumber, Select } from "antd";
import type { FormListFieldData } from "antd/es/form";
import { Localize, localizeByKeyStatic } from "components/service";
import Icons from "components/icons";
import type {
  TCommunicationChangeAction,
  TCommunicationContextGroup,
  TCommunicationPreviewLogisticsRow,
  TCommunicationQuestion,
  TCommunicationReferenceOption,
  TCommunicationRequestAction,
  TCommunicationRequestGroup,
  TCommunicationRequestStatus,
  TCommunicationSuggestionStatus,
  TCommunicationTaskFormValues,
} from "../../helpers/communication-types-helper";
import {
  buildContextGroupLookupKey,
  getEffectiveEquipmentGroupId,
  getEquipmentSuggestionMeta,
  type TEditableChangeGroup,
} from "./communication-task-form-state";

type TStandToneProps = {
  toneClassName: string;
  surfaceClassName: string;
  style?: React.CSSProperties;
};

interface ICommunicationTaskRequestGroupsProps {
  groups: TCommunicationRequestGroup[];
  shouldColorizeStandGroups: boolean;
  formatStandGroupTitle: (value?: string | null) => string;
  getCountLabel: (count: number, singularKey: string, pluralKey: string) => string;
  getRequestStatusTone: (status: TCommunicationRequestStatus) => string;
  getStandToneProps: (standNumber?: string | null, enabled?: boolean) => TStandToneProps;
  requestActionLabelKeyMap: Record<TCommunicationRequestAction, string>;
  requestStatusLabelKeyMap: Record<TCommunicationRequestStatus, string>;
}

interface ICommunicationTaskClarificationsPanelProps {
  resolvedQuestions: TCommunicationTaskFormValues["questions"];
  openQuestionsCount: number;
  unresolvedReviewCount: number;
  followUpStandNumber?: string;
  clarificationStandContextLabel?: string;
  formatCanonicalStandNumberLabel: (value?: string | null) => string;
  questionStatusLabelMap: Record<TCommunicationQuestion["status"], string>;
  questionStatusOptions: Array<{ value: TCommunicationQuestion["status"]; label: string }>;
  handleQuestionStatusToggle: (index: number, checked: boolean) => void;
}

interface ICommunicationTaskLogisticsTableProps {
  rows: TCommunicationPreviewLogisticsRow[];
  emptyKey: string;
  productLabelKey?: string;
  shouldColorizeStandGroups: boolean;
  excludedContextGroups?: TCommunicationContextGroup[];
  formatCanonicalStandNumberLabel: (value?: string | null) => string;
  formatStandGroupTitle: (value?: string | null) => string;
  getCountLabel: (count: number, singularKey: string, pluralKey: string) => string;
  getStandToneProps: (standNumber?: string | null, enabled?: boolean) => TStandToneProps;
  actionLabelKeyMap: Record<TCommunicationPreviewLogisticsRow["action"], string>;
}

interface ICommunicationTaskEquipmentChangesEditorProps {
  fields: FormListFieldData[];
  hasMultiStandEditing: boolean;
  editableChangeGroups: TEditableChangeGroup[];
  primaryEventName?: string;
  primaryCompanyName?: string;
  primaryStandNumber?: string;
  watchedEquipmentChanges?: TCommunicationTaskFormValues["equipment_changes"];
  actionOptions: Array<{ value: TCommunicationChangeAction; label: string }>;
  standSelectOptions: Array<{ value: string; label: string }>;
  equipmentSelectOptions: Array<TCommunicationReferenceOption & { searchLabel: string }>;
  equipmentOptionsMap: Map<string, string>;
  openEquipmentField: number | null;
  shouldColorizeStandGroups: boolean;
  formatStandGroupTitle: (value?: string | null) => string;
  getCountLabel: (count: number, singularKey: string, pluralKey: string) => string;
  getStandToneProps: (standNumber?: string | null, enabled?: boolean) => TStandToneProps;
  suggestionStatusLabelKeyMap: Record<TCommunicationSuggestionStatus, string>;
  validateEquipmentSelection: (index: number, value?: string) => Promise<void>;
  handleStandSelectChange: (index: number, value?: string) => void;
  handleConfirmEquipmentSuggestion: (index: number) => void;
  handleChooseAnotherEquipment: (index: number) => void;
  handleEquipmentSelectChange: (index: number, equipmentGroupId?: string) => void;
  handleEquipmentDropdownVisibleChange: (index: number, isOpen: boolean) => void;
  handleRemoveEquipmentChange: (index: number) => void;
  handleAddEquipmentChange: () => void;
}

interface ICommunicationTaskReplyDraftPanelProps {
  showPanel: boolean;
  hasReplyHandoffNote: boolean;
  followUpStandNumber?: string;
  formatCanonicalStandNumberLabel: (value?: string | null) => string;
  replyDraftRecipientLabel: string;
  replyDraftSubject: string;
  replyDraftTemplates: Array<{ key: string; labelKey: string }>;
  canOpenReplyDraft: boolean;
  handleApplyReplyDraftTemplate: (templateKey: string) => void;
  handleOpenReplyDraft: () => void;
  handleShowReplyHandoffNote: () => void;
}

export const CommunicationTaskRequestGroups: React.FC<ICommunicationTaskRequestGroupsProps> = ({
  groups,
  shouldColorizeStandGroups,
  formatStandGroupTitle,
  getCountLabel,
  getRequestStatusTone,
  getStandToneProps,
  requestActionLabelKeyMap,
  requestStatusLabelKeyMap,
}) => {
  if (!groups.length) {
    return (
      <div className="communication-task-form__preview-empty">
        <Localize>COMMUNICATION.Text_NoExtractedChanges</Localize>
      </div>
    );
  }

  return (
    <div className="communication-task-form__request-groups">
      {groups.map((group) => {
        const reviewRequests = group.requests.filter(
          (request) => request.status !== "unidentified" && request.action !== "question"
        );
        const unidentifiedRequests = group.requests.filter(
          (request) => request.status === "unidentified"
        );
        const questionRequests = group.requests.filter(
          (request) => request.status !== "unidentified" && request.action === "question"
        );
        const groupSummary = [
          group.change_count > 0
            ? getCountLabel(
              group.change_count,
              "COMMUNICATION.RequestGroup_ItemSingle",
              "COMMUNICATION.RequestGroup_Items"
            )
            : null,
          group.question_count > 0
            ? getCountLabel(
              group.question_count,
              "COMMUNICATION.RequestGroup_QuestionSingle",
              "COMMUNICATION.RequestGroup_Questions"
            )
            : null,
          group.unidentified_count > 0
            ? getCountLabel(
              group.unidentified_count,
              "COMMUNICATION.RequestGroup_UnidentifiedSingle",
              "COMMUNICATION.RequestGroup_Unidentified"
            )
            : null,
        ].filter((value): value is string => Boolean(value));

        const requestSections = [
          {
            key: "review",
            titleKey: "COMMUNICATION.RequestGroup_SectionReview",
            hintKey: "COMMUNICATION.RequestGroup_SectionReviewHint",
            tone: "review",
            requests: reviewRequests,
          },
          {
            key: "unidentified",
            titleKey: "COMMUNICATION.RequestGroup_SectionUnidentified",
            hintKey: "COMMUNICATION.RequestGroup_SectionUnidentifiedHint",
            tone: "warning",
            requests: unidentifiedRequests,
          },
          {
            key: "questions",
            titleKey: "COMMUNICATION.RequestGroup_SectionQuestions",
            hintKey: "COMMUNICATION.RequestGroup_SectionQuestionsHint",
            tone: "question",
            requests: questionRequests,
          },
        ].filter((section) => section.requests.length > 0);
        const standTone = getStandToneProps(group.stand_number, shouldColorizeStandGroups);

        return (
          <div
            key={group.id}
            className={`communication-task-form__request-group ${standTone.surfaceClassName}`.trim()}
            style={standTone.style}
          >
            <div className="communication-task-form__request-group-header">
              <div className="communication-task-form__request-group-copy">
                <strong
                  className={`communication-task-form__request-group-title ${standTone.toneClassName}`.trim()}
                  style={standTone.style}
                >
                  {formatStandGroupTitle(group.stand_number)}
                </strong>
                <span className="communication-task-form__request-group-subtitle">
                  {[
                    group.company_name || localizeByKeyStatic("COMMUNICATION.RequestGroup_CompanyFallback"),
                    group.event_name || localizeByKeyStatic("COMMUNICATION.RequestGroup_EventFallback"),
                  ].join(" / ")}
                </span>
              </div>
              <div className="communication-task-form__request-group-badges">
                {groupSummary.map((summary) => (
                  <span
                    key={`${group.id}-${summary}`}
                    className="communication-task-form__request-group-badge"
                  >
                    {summary}
                  </span>
                ))}
              </div>
            </div>

            <div className="communication-task-form__request-group-list">
              {requestSections.map((section) => (
                <div
                  key={`${group.id}-${section.key}`}
                  className={`communication-task-form__request-section communication-task-form__request-section--${section.tone}`}
                >
                  <div className="communication-task-form__request-section-header">
                    <div className="communication-task-form__request-section-copy">
                      <strong className="communication-task-form__request-section-title">
                        <Localize>{section.titleKey}</Localize>
                      </strong>
                      <small className="communication-task-form__request-section-hint">
                        <Localize>{section.hintKey}</Localize>
                      </small>
                    </div>
                    <span className="communication-task-form__request-section-count">
                      {getCountLabel(
                        section.requests.length,
                        "COMMUNICATION.RequestGroup_ItemSingle",
                        "COMMUNICATION.RequestGroup_Items"
                      )}
                    </span>
                  </div>

                  <div className="communication-task-form__request-section-list">
                    {section.requests.map((request) => {
                      const statusTone = getRequestStatusTone(request.status);
                      const requestTitle = request.item_name || request.item_notes || "\u2014";

                      return (
                        <div key={request.id} className="communication-task-form__request-row">
                          <span
                            className={`communication-task-form__action-tag communication-task-form__action-tag--${request.action}`}
                          >
                            <Localize>{requestActionLabelKeyMap[request.action]}</Localize>
                          </span>

                          <div className="communication-task-form__request-row-copy">
                            <strong title={requestTitle}>{requestTitle}</strong>
                            {request.item_notes && request.item_notes !== request.item_name && (
                              <small title={request.item_notes}>
                                {localizeByKeyStatic("COMMUNICATION.RequestGroup_ItemNotes", {
                                  note: request.item_notes,
                                })}
                              </small>
                            )}
                          </div>

                          <div className="communication-task-form__request-row-side">
                            <span
                              className={`communication-task-form__request-status communication-task-form__request-status--${statusTone}`}
                            >
                              <Localize>{requestStatusLabelKeyMap[request.status]}</Localize>
                            </span>
                            <span className="communication-task-form__request-confidence">
                              {localizeByKeyStatic("COMMUNICATION.RequestGroup_Confidence", {
                                count: request.confidence,
                              })}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const CommunicationTaskLogisticsTable: React.FC<ICommunicationTaskLogisticsTableProps> = ({
  rows,
  emptyKey,
  productLabelKey = "COMMUNICATION.Preview_Product",
  shouldColorizeStandGroups,
  excludedContextGroups = [],
  formatCanonicalStandNumberLabel,
  formatStandGroupTitle,
  getCountLabel,
  getStandToneProps,
  actionLabelKeyMap,
}) => {
  if (!rows.length) {
    return (
      <div className="communication-task-form__preview-empty">
        <Localize>{emptyKey}</Localize>
      </div>
    );
  }

  const eventLabel = localizeByKeyStatic("COMMUNICATION.Filter_Event");
  const actionLabel = localizeByKeyStatic("FORMS.Input_Label_Action");
  const productLabel = localizeByKeyStatic(productLabelKey);
  const companyLabel = localizeByKeyStatic("FORMS.Input_Label_CompanyName");
  const standLabel = localizeByKeyStatic("FORMS.Input_Label_StandNumber");
  const quantityLabel = localizeByKeyStatic("COMMUNICATION.Preview_TotalQty");
  const dateLabel = localizeByKeyStatic("COMMUNICATION.Preview_EmailDate");
  const groupedRows = rows.reduce<Array<{
    key: string;
    stand_number: string;
    company_name: string;
    event_name: string;
    rows: TCommunicationPreviewLogisticsRow[];
  }>>((groups, row) => {
    const groupKey = buildContextGroupLookupKey(row.event_name, row.company_name, row.stand_number);
    const existingGroup = groups.find((group) => group.key === groupKey);

    if (existingGroup) {
      existingGroup.rows.push(row);
      return groups;
    }

    groups.push({
      key: groupKey,
      stand_number: row.stand_number || "",
      company_name: row.company_name || "",
      event_name: row.event_name || "",
      rows: [ row ],
    });

    return groups;
  }, []);
  const shouldRenderGroupedRows = shouldColorizeStandGroups && groupedRows.length > 1;
  const excludedContextSummary = excludedContextGroups
    .map((group) => [ group.event_name, group.company_name, formatCanonicalStandNumberLabel(group.stand_number) ].filter(Boolean).join(" / "))
    .join(", ");

  const renderLogisticsRow = (row: TCommunicationPreviewLogisticsRow) => {
    const standTone = getStandToneProps(row.stand_number, shouldColorizeStandGroups);

    return (
      <div key={row.id} className="communication-task-form__logistics-row">
        <span className="communication-task-form__logistics-cell" data-label={eventLabel}>
          <span
            className="communication-task-form__logistics-cell-copy"
            title={row.event_name || "\u2014"}
          >
            {row.event_name || "\u2014"}
          </span>
        </span>
        <span
          className="communication-task-form__logistics-cell communication-task-form__logistics-cell--action"
          data-label={actionLabel}
        >
          <span
            className={`communication-task-form__action-tag communication-task-form__action-tag--${row.action}`}
          >
            <Localize>{actionLabelKeyMap[row.action]}</Localize>
          </span>
        </span>
        <span className="communication-task-form__logistics-cell" data-label={productLabel}>
          <span
            className="communication-task-form__logistics-cell-copy"
            title={row.product || "\u2014"}
          >
            {row.product || "\u2014"}
          </span>
        </span>
        <span className="communication-task-form__logistics-cell" data-label={companyLabel}>
          <span
            className="communication-task-form__logistics-cell-copy"
            title={row.company_name || "\u2014"}
          >
            {row.company_name || "\u2014"}
          </span>
        </span>
        <span
          className="communication-task-form__logistics-cell communication-task-form__logistics-cell--stand"
          data-label={standLabel}
        >
          <span
            className={`communication-task-form__logistics-cell-copy communication-task-form__logistics-cell-copy--stand ${standTone.toneClassName}`.trim()}
            style={standTone.style}
            title={formatCanonicalStandNumberLabel(row.stand_number)}
          >
            {formatCanonicalStandNumberLabel(row.stand_number)}
          </span>
        </span>
        <span
          className="communication-task-form__logistics-cell communication-task-form__logistics-cell--qty"
          data-label={quantityLabel}
        >
          {row.quantity}
        </span>
        <span
          className="communication-task-form__logistics-cell communication-task-form__logistics-cell--date"
          data-label={dateLabel}
        >
          {row.received_at}
        </span>
      </div>
    );
  };

  return (
    <div className="communication-task-form__logistics-table">
      <div className="communication-task-form__logistics-head">
        <strong className="communication-task-form__logistics-head-cell">
          <span
            className="communication-task-form__logistics-head-copy"
            title={eventLabel}
          >
            <Localize>COMMUNICATION.Filter_Event</Localize>
          </span>
        </strong>
        <strong className="communication-task-form__logistics-head-cell communication-task-form__logistics-head-cell--action">
          <span
            className="communication-task-form__logistics-head-copy"
            title={actionLabel}
          >
            <Localize>FORMS.Input_Label_Action</Localize>
          </span>
        </strong>
        <strong className="communication-task-form__logistics-head-cell">
          <span
            className="communication-task-form__logistics-head-copy"
            title={productLabel}
          >
            <Localize>{productLabelKey}</Localize>
          </span>
        </strong>
        <strong className="communication-task-form__logistics-head-cell">
          <span
            className="communication-task-form__logistics-head-copy"
            title={companyLabel}
          >
            <Localize>FORMS.Input_Label_CompanyName</Localize>
          </span>
        </strong>
        <strong className="communication-task-form__logistics-head-cell communication-task-form__logistics-head-cell--stand">
          <span
            className="communication-task-form__logistics-head-copy"
            title={standLabel}
          >
            <Localize>FORMS.Input_Label_StandNumber</Localize>
          </span>
        </strong>
        <strong className="communication-task-form__logistics-head-cell communication-task-form__logistics-head-cell--qty">
          <span
            className="communication-task-form__logistics-head-copy"
            title={quantityLabel}
          >
            <Localize>COMMUNICATION.Preview_TotalQty</Localize>
          </span>
        </strong>
        <strong className="communication-task-form__logistics-head-cell communication-task-form__logistics-head-cell--date">
          <span
            className="communication-task-form__logistics-head-copy"
            title={dateLabel}
          >
            <Localize>COMMUNICATION.Preview_EmailDate</Localize>
          </span>
        </strong>
      </div>

      {excludedContextGroups.length > 0 && (
        <div className="communication-task-form__logistics-notice">
          <span className="communication-task-form__logistics-notice-label">
            {excludedContextGroups.length === 1 ? "1 stand excluded by manager" : `${excludedContextGroups.length} stands excluded by manager`}
          </span>
          <span
            className="communication-task-form__logistics-notice-copy"
            title={excludedContextSummary}
          >
            {excludedContextSummary}
          </span>
        </div>
      )}

      {shouldRenderGroupedRows
        ? groupedRows.map((group) => {
          const standTone = getStandToneProps(group.stand_number, true);

          return (
            <React.Fragment key={group.key}>
              <div
                className={`communication-task-form__logistics-group-header ${standTone.surfaceClassName}`.trim()}
                style={standTone.style}
              >
                <div className="communication-task-form__logistics-group-copy">
                  <strong
                    className={`communication-task-form__logistics-group-title ${standTone.toneClassName}`.trim()}
                    style={standTone.style}
                  >
                    {formatStandGroupTitle(group.stand_number)}
                  </strong>
                  <span className="communication-task-form__logistics-group-subtitle">
                    {[
                      group.company_name || "\u2014",
                      group.event_name || "\u2014",
                    ].join(" / ")}
                  </span>
                </div>
                <span className="communication-task-form__logistics-group-count">
                  {getCountLabel(
                    group.rows.length,
                    "COMMUNICATION.RequestGroup_ItemSingle",
                    "COMMUNICATION.RequestGroup_Items"
                  )}
                </span>
              </div>
              {group.rows.map(renderLogisticsRow)}
            </React.Fragment>
          );
        })
        : rows.map(renderLogisticsRow)}
    </div>
  );
};

export const CommunicationTaskEquipmentChangesEditor: React.FC<ICommunicationTaskEquipmentChangesEditorProps> = ({
  fields,
  hasMultiStandEditing,
  editableChangeGroups,
  primaryEventName,
  primaryCompanyName,
  primaryStandNumber,
  watchedEquipmentChanges,
  actionOptions,
  standSelectOptions,
  equipmentSelectOptions,
  equipmentOptionsMap,
  openEquipmentField,
  shouldColorizeStandGroups,
  formatStandGroupTitle,
  getCountLabel,
  getStandToneProps,
  suggestionStatusLabelKeyMap,
  validateEquipmentSelection,
  handleStandSelectChange,
  handleConfirmEquipmentSuggestion,
  handleChooseAnotherEquipment,
  handleEquipmentSelectChange,
  handleEquipmentDropdownVisibleChange,
  handleRemoveEquipmentChange,
  handleAddEquipmentChange,
}) => {
  const fieldMap = new Map(fields.map((field) => [ Number(field.name), field ]));
  const changeGroupsToRender = hasMultiStandEditing
    ? editableChangeGroups
    : [{
      key: "single-stand",
      eventName: primaryEventName,
      companyName: primaryCompanyName,
      standNumber: primaryStandNumber,
      itemIndexes: fields.map((field) => Number(field.name)),
    }];

  const renderEquipmentChangeField = (field: FormListFieldData) => {
    const fieldIndex = Number(field.name);
    const currentChange = watchedEquipmentChanges?.[fieldIndex];
    const equipmentLabel =
      equipmentOptionsMap.get(getEffectiveEquipmentGroupId(currentChange) || "") || "";
    const suggestionMeta = currentChange ? getEquipmentSuggestionMeta(currentChange) : null;

    return (
      <div
        key={field.key}
        className={`row-grid grid-gap-10 row-gap-0 communication-task-form__row communication-task-form__change-grid${hasMultiStandEditing ? " communication-task-form__change-grid--multi-stand" : ""}${suggestionMeta ? ` communication-task-form__change-grid--${suggestionMeta.status}` : ""}`}
      >
        <Form.Item
          name={[field.name, "item_label"]}
          hidden={true}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name={[field.name, "equipment_id"]}
          hidden={true}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name={[field.name, "event_name"]}
          hidden={true}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name={[field.name, "company_name"]}
          hidden={true}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name={[field.name, "equipment_title"]}
          hidden={true}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name={[field.name, "suggested_equipment_group_id"]}
          hidden={true}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name={[field.name, "suggestion_status"]}
          hidden={true}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name={[field.name, "action"]}
          className="communication-task-form__change-field communication-task-form__change-field--action"
          label={<Localize>FORMS.Input_Label_Action</Localize>}
        >
          <Select suffixIcon={<Icons.Arrow />} options={actionOptions} />
        </Form.Item>
        {hasMultiStandEditing && (
          <Form.Item
            name={[field.name, "stand_number"]}
            className="communication-task-form__change-field communication-task-form__change-field--stand"
            label={<Localize>FORMS.Input_Label_StandNumber</Localize>}
            rules={[{
              required: true,
              message: localizeByKeyStatic("FORM_RULES.Required_StandNumber"),
            }]}
          >
            <Select
              suffixIcon={<Icons.Arrow />}
              options={standSelectOptions}
              placeholder={localizeByKeyStatic("FORMS.Input_Placeholder_Choose")}
              onChange={(value) => handleStandSelectChange(fieldIndex, value)}
            />
          </Form.Item>
        )}
        <Form.Item
          name={[field.name, "equipment_group_id"]}
          className="communication-task-form__change-field communication-task-form__change-field--equipment"
          label={(
            <div className="communication-task-form__equipment-label">
              <span className="communication-task-form__equipment-label-text">
                <Localize>FORMS.Input_Label_Equipment</Localize>
              </span>

              {suggestionMeta && (
                <div
                  className={`communication-task-form__equipment-suggestion communication-task-form__equipment-suggestion--${suggestionMeta.status}`}
                >
                  <span className="communication-task-form__equipment-suggestion-badge">
                    <Localize>
                      {suggestionMeta.status === "pending"
                        ? "COMMUNICATION.EquipmentSuggestion_Badge"
                        : suggestionMeta.status === "confirmed"
                          ? "COMMUNICATION.EquipmentSuggestion_ConfirmedBadge"
                          : "COMMUNICATION.EquipmentSuggestion_ChangedBadge"}
                    </Localize>
                  </span>

                  {suggestionMeta.status === "pending" ? (
                    <div className="communication-task-form__equipment-suggestion-actions">
                      <Button
                        type="link"
                        icon={<Icons.Check />}
                        onClick={() => handleConfirmEquipmentSuggestion(fieldIndex)}
                      >
                        <Localize>GLOBAL.Button_Text_Confirm</Localize>
                      </Button>
                      <Button
                        type="link"
                        onClick={() => handleChooseAnotherEquipment(fieldIndex)}
                      >
                        <Localize>COMMUNICATION.Button_ChooseAnother</Localize>
                      </Button>
                    </div>
                  ) : (
                    <span className="communication-task-form__equipment-suggestion-status">
                      <Localize>{suggestionStatusLabelKeyMap[suggestionMeta.status]}</Localize>
                    </span>
                  )}
                </div>
              )}
            </div>
          )}
          rules={[{
            validator: (_, value) => validateEquipmentSelection(fieldIndex, value),
          }]}
        >
          <Select
            key={`${field.key}-${currentChange?.equipment_group_id || currentChange?.suggested_equipment_group_id || "empty"}-${equipmentSelectOptions.length}`}
            suffixIcon={<Icons.Arrow />}
            allowClear
            showSearch
            open={openEquipmentField === fieldIndex ? true : undefined}
            options={equipmentSelectOptions}
            optionLabelProp="label"
            optionFilterProp="searchLabel"
            popupClassName="communication-task-form__equipment-dropdown"
            placeholder={localizeByKeyStatic("FORMS.Input_Placeholder_Choose")}
            title={equipmentLabel.length > 40 ? equipmentLabel : undefined}
            onChange={(value) => handleEquipmentSelectChange(fieldIndex, value)}
            onDropdownVisibleChange={(isOpen) =>
              handleEquipmentDropdownVisibleChange(fieldIndex, isOpen)
            }
          />
        </Form.Item>
        <Form.Item
          name={[field.name, "qty"]}
          className="communication-task-form__change-field communication-task-form__change-field--qty"
          label={<Localize>FORMS.Input_Label_Amount</Localize>}
          rules={[{ required: true, message: localizeByKeyStatic("FORM_RULES.Required_Amount") }]}
        >
          <InputNumber min={1} style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item
          name={[field.name, "color"]}
          className="communication-task-form__change-field communication-task-form__change-field--color"
          label={<Localize>FORMS.Input_Label_Color</Localize>}
        >
          <Input />
        </Form.Item>
        <Form.Item
          className="communication-task-form__change-field communication-task-form__change-field--delete"
          label=" "
        >
          <Button
            className="communication-task-form__change-delete-button"
            onClick={() => handleRemoveEquipmentChange(fieldIndex)}
          >
            <span className="communication-task-form__change-delete-button-icon">
              <Icons.Delete />
            </span>
            <span className="communication-task-form__change-delete-button-label">
              <Localize>GLOBAL.Button_Text_Remove</Localize>
            </span>
          </Button>
        </Form.Item>
      </div>
    );
  };

  return (
    <>
      <div className="communication-task-form__change-groups">
        {changeGroupsToRender.map((group) => {
          const groupFields = group.itemIndexes
            .map((index) => fieldMap.get(index))
            .filter(Boolean) as FormListFieldData[];
          const standTone = getStandToneProps(group.standNumber, shouldColorizeStandGroups);

          if (!groupFields.length) {
            return null;
          }

          return (
            <div
              key={group.key}
              className={`communication-task-form__change-group ${standTone.surfaceClassName}`.trim()}
              style={standTone.style}
            >
              {hasMultiStandEditing && (
                <div className="communication-task-form__change-group-header">
                  <div className="communication-task-form__request-group-copy">
                    <strong
                      className={`communication-task-form__change-group-title ${standTone.toneClassName}`.trim()}
                      style={standTone.style}
                    >
                      {formatStandGroupTitle(group.standNumber)}
                    </strong>
                    {(group.companyName || group.eventName) && (
                      <span className="communication-task-form__request-group-subtitle">
                        {[ group.companyName || "\u2014", group.eventName || "\u2014" ].join(" / ")}
                      </span>
                    )}
                  </div>
                  <div className="communication-task-form__change-group-actions">
                    <Button
                      className="light-bg communication-task-form__change-group-tool-btn"
                      icon={<Icons.Note />}
                      disabled
                    >
                      <span className="ellipsis">
                        <Localize>COMMUNICATION.Button_ViewStandDrawing</Localize>
                      </span>
                    </Button>
                    <span className="communication-task-form__change-group-count">
                      {getCountLabel(
                        groupFields.length,
                        "COMMUNICATION.RequestGroup_ItemSingle",
                        "COMMUNICATION.RequestGroup_Items"
                      )}
                    </span>
                  </div>
                </div>
              )}

              <div className="communication-task-form__change-group-list">
                {groupFields.map((field) => renderEquipmentChangeField(field))}
              </div>
            </div>
          );
        })}
      </div>

      <div className="communication-task-form__add-actions">
        <div className="communication-task-form__add-actions-primary">
          <Button
            className="light-bg"
            icon={<Icons.Plus />}
            onClick={handleAddEquipmentChange}
          >
            <span className="ellipsis">
              <Localize>COMMUNICATION.Button_AddChange</Localize>
            </span>
          </Button>
        </div>

        {/*
        (!showStandUpdates || !showManagerNote) && (
          <div className="communication-task-form__add-actions-secondary">
            {!showStandUpdates && (
              <Button
                className="light-bg"
                icon={<Icons.Plus />}
                onClick={() => setShowStandUpdates(true)}
              >
                <span className="ellipsis">
                  <Localize>COMMUNICATION.Button_AddStandUpdate</Localize>
                </span>
              </Button>
            )}

            {!showManagerNote && (
              <Button
                className="light-bg"
                icon={<Icons.Plus />}
                onClick={() => setShowManagerNote(true)}
              >
                <span className="ellipsis">
                  <Localize>COMMUNICATION.Button_AddManagerNote</Localize>
                </span>
              </Button>
            )}
          </div>
        )}
        */}
      </div>
    </>
  );
};

export const CommunicationTaskReplyDraftPanel: React.FC<ICommunicationTaskReplyDraftPanelProps> = ({
  showPanel,
  hasReplyHandoffNote,
  followUpStandNumber,
  formatCanonicalStandNumberLabel,
  replyDraftRecipientLabel,
  replyDraftSubject,
  replyDraftTemplates,
  canOpenReplyDraft,
  handleApplyReplyDraftTemplate,
  handleOpenReplyDraft,
  handleShowReplyHandoffNote,
}) => {
  if (!showPanel) {
    return (
      <div className="communication-task-form__handoff-note-add">
        <Button
          className="light-bg"
          icon={<Icons.Plus />}
          onClick={handleShowReplyHandoffNote}
        >
          <span className="ellipsis">
            <Localize>COMMUNICATION.HandoffNote_Add</Localize>
          </span>
        </Button>
      </div>
    );
  }

  return (
    <div className="communication-task-form__preview-card communication-task-form__preview-card--full communication-task-form__preview-card--handoff communication-task-form__reply-draft-card communication-task-form__reply-draft-card--workflow">
      <div className="communication-task-form__preview-header">
        <span className="communication-task-form__preview-title">
          <Localize>COMMUNICATION.HandoffNote_Title</Localize>
        </span>
        <span className={`communication-task-form__preview-count${hasReplyHandoffNote ? "" : " communication-task-form__preview-count--muted"}`}>
          <Localize>
            {hasReplyHandoffNote
              ? "COMMUNICATION.HandoffNote_StatusPrepared"
              : "COMMUNICATION.HandoffNote_StatusEmpty"}
          </Localize>
        </span>
      </div>

      <div className="communication-task-form__reply-draft-meta">
        {followUpStandNumber && (
          <span className="communication-task-form__question-meta-chip">
            {localizeByKeyStatic("COMMUNICATION.QuestionsWorkflow_RelatedStand", {
              stand: formatCanonicalStandNumberLabel(followUpStandNumber),
            })}
          </span>
        )}
        <span className="communication-task-form__question-meta-chip">
          <Localize>COMMUNICATION.QuestionCard_FollowUp</Localize>
        </span>
      </div>

      <div className="communication-task-form__reply-draft-addresses">
        <div className="communication-task-form__reply-draft-address">
          <span className="communication-task-form__reply-draft-address-label">
            <Localize>COMMUNICATION.ReplyDraft_To</Localize>
          </span>
          <strong
            className="communication-task-form__reply-draft-address-value"
            title={replyDraftRecipientLabel}
          >
            {replyDraftRecipientLabel}
          </strong>
        </div>
        <div className="communication-task-form__reply-draft-address">
          <span className="communication-task-form__reply-draft-address-label">
            <Localize>COMMUNICATION.ReplyDraft_Subject</Localize>
          </span>
          <strong
            className="communication-task-form__reply-draft-address-value"
            title={replyDraftSubject}
          >
            {replyDraftSubject}
          </strong>
        </div>
      </div>

      <div className="communication-task-form__reply-draft-templates">
        <span className="communication-task-form__reply-draft-template-label">
          <Localize>COMMUNICATION.ReplyDraft_QuickReplies</Localize>
        </span>
        <div className="communication-task-form__reply-draft-template-list">
          {replyDraftTemplates.map((template) => (
            <Button
              key={template.key}
              className="light-bg communication-task-form__reply-draft-template"
              onClick={() => handleApplyReplyDraftTemplate(template.key)}
            >
              <span className="ellipsis">
                <Localize>{template.labelKey}</Localize>
              </span>
            </Button>
          ))}
        </div>
      </div>

      <Form.Item
        name="reply_handoff_note"
        className="communication-task-form__handoff-note-field"
      >
        <Input.TextArea
          rows={3}
          placeholder={localizeByKeyStatic("COMMUNICATION.HandoffNote_Placeholder")}
        />
      </Form.Item>

      <div className="communication-task-form__reply-draft-footer">
        <small className="communication-task-form__reply-draft-note">
          <Localize>COMMUNICATION.ReplyDraft_OpenMailAppHint</Localize>
        </small>

        <div className="communication-task-form__reply-draft-actions">
          <Button
            type="primary"
            className="communication-task-form__reply-draft-open"
            disabled={!canOpenReplyDraft}
            onClick={handleOpenReplyDraft}
          >
            <span className="ellipsis">
              <Localize>COMMUNICATION.ReplyDraft_OpenMailApp</Localize>
            </span>
          </Button>
        </div>
      </div>
    </div>
  );
};

export const CommunicationTaskClarificationsPanel: React.FC<ICommunicationTaskClarificationsPanelProps> = ({
  resolvedQuestions,
  openQuestionsCount,
  unresolvedReviewCount,
  followUpStandNumber,
  clarificationStandContextLabel,
  formatCanonicalStandNumberLabel,
  questionStatusLabelMap,
  questionStatusOptions,
  handleQuestionStatusToggle,
}) => (
  <Form.List name="questions">
    {(fields, { remove }) => (
      <div className="communication-task-form__questions-panel communication-task-form__questions-panel--compact communication-task-form__questions-panel--workflow">
        <div className="communication-task-form__questions-header">
          <div className="communication-task-form__questions-copy">
            <span className="communication-task-form__questions-eyebrow">
              <Localize>COMMUNICATION.Preview_QuestionsWorking</Localize>
            </span>
            <strong className="communication-task-form__questions-title">
              <Localize
                questions={resolvedQuestions.length}
                open={openQuestionsCount}
              >
                COMMUNICATION.Text_QuestionsSummary
              </Localize>
            </strong>
            <div className="communication-task-form__questions-meta">
              {followUpStandNumber && (
                <span className="communication-task-form__question-meta-chip">
                  {localizeByKeyStatic("COMMUNICATION.QuestionsWorkflow_RelatedStand", {
                    stand: formatCanonicalStandNumberLabel(followUpStandNumber),
                  })}
                </span>
              )}
              <span
                className={`communication-task-form__question-meta-chip${openQuestionsCount > 0 ? " communication-task-form__question-meta-chip--warning" : " communication-task-form__question-meta-chip--muted"}`}
              >
                {resolvedQuestions.length > 0
                  ? localizeByKeyStatic("COMMUNICATION.Text_QuestionsSummary", {
                    open: openQuestionsCount,
                    questions: resolvedQuestions.length,
                  })
                  : localizeByKeyStatic("COMMUNICATION.Text_NoQuestions")}
              </span>
              {unresolvedReviewCount > 0 && (
                <span className="communication-task-form__question-meta-chip communication-task-form__question-meta-chip--warning">
                  {localizeByKeyStatic("COMMUNICATION.Step1Summary_UnresolvedHint", {
                    count: unresolvedReviewCount,
                  })}
                </span>
              )}
            </div>
          </div>

          <div className="communication-task-form__questions-actions">
            <span
              className={`communication-task-form__questions-badge${openQuestionsCount > 0 ? " communication-task-form__questions-badge--warning" : ""}`}
            >
              {fields.length > 0 ? (
                <Localize
                  questions={resolvedQuestions.length}
                  open={openQuestionsCount}
                >
                  COMMUNICATION.Text_QuestionsSummary
                </Localize>
              ) : (
                <Localize>COMMUNICATION.Text_NoQuestions</Localize>
              )}
            </span>
          </div>
        </div>

        <div className="communication-task-form__question-list">
          {fields.length === 0 && (
            <div className="communication-task-form__questions-empty">
              <Localize>COMMUNICATION.Text_NoQuestions</Localize>
            </div>
          )}

          {fields.map((field, index) => {
            const currentQuestion = resolvedQuestions?.[field.name];
            const questionStatus = currentQuestion?.status || "open";

            return (
              <div
                key={field.key}
                className={`communication-task-form__question-card communication-task-form__question-card--${questionStatus}`}
              >
                <div className="communication-task-form__question-card-header">
                  <div className="communication-task-form__question-card-copy">
                    <div className="communication-task-form__question-card-title-row">
                      <strong className="communication-task-form__question-card-title">
                        {`${localizeByKeyStatic("COMMUNICATION.Field_Clarification")} #${index + 1}`}
                      </strong>
                      <span
                        className={`communication-task-form__question-card-state communication-task-form__question-card-state--${questionStatus}`}
                      >
                        {questionStatusLabelMap[questionStatus]}
                      </span>
                    </div>
                    <div className="communication-task-form__question-card-meta">
                      {clarificationStandContextLabel && (
                        <span className="communication-task-form__question-meta-chip">
                          {clarificationStandContextLabel}
                        </span>
                      )}
                      <span className="communication-task-form__question-meta-chip communication-task-form__question-meta-chip--warning">
                        <Localize>COMMUNICATION.QuestionCard_AffectsChanges</Localize>
                      </span>
                    </div>
                  </div>
                </div>

                <div className="communication-task-form__question-row">
                  <span className="communication-task-form__question-label">
                    <span className="communication-task-form__question-label-required">*</span>
                    <Localize>COMMUNICATION.QuestionCard_RequestLabel</Localize>
                  </span>
                  <Form.Item
                    className="communication-task-form__question-main"
                    name={[field.name, "text"]}
                    rules={[
                      {
                        required: true,
                        message: localizeByKeyStatic("COMMUNICATION.Validation_Question"),
                      },
                    ]}
                  >
                    <Input.TextArea rows={3} />
                  </Form.Item>

                  <div className="communication-task-form__question-side">
                    <div className="communication-task-form__question-side-status">
                      <span className="communication-task-form__question-side-label">
                        <Localize>COMMUNICATION.QuestionCard_StatusLabel</Localize>
                      </span>
                      <Form.Item
                        className="communication-task-form__question-card-status communication-task-form__question-card-status--stacked"
                        name={[field.name, "status"]}
                      >
                        <Select suffixIcon={<Icons.Arrow />} options={questionStatusOptions} />
                      </Form.Item>
                    </div>

                    <div className="communication-task-form__question-side-actions">
                      <Checkbox
                        className="communication-task-form__question-toggle communication-task-form__question-toggle--inline"
                        checked={questionStatus === "reviewed"}
                        onChange={(event) =>
                          handleQuestionStatusToggle(field.name, event.target.checked)
                        }
                      >
                        <Localize>COMMUNICATION.QuestionStatus_Reviewed</Localize>
                      </Checkbox>

                      <Button
                        type="text"
                        className="communication-task-form__question-delete communication-task-form__question-delete--link"
                        icon={<Icons.Delete />}
                        onClick={() => remove(field.name)}
                      >
                        <span className="ellipsis">
                          <Localize>GLOBAL.Button_Text_Remove</Localize>
                        </span>
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    )}
  </Form.List>
);
